<template>
	<v-dialog
		:value="value"
		@input="$emit('input', $event)"
		max-width="700"
		persistent>
        <v-card class="background-2 round-card">
			<v-card-title class="pa-3">
				<v-icon left>mdi-whatsapp</v-icon>
				<p class="pl-2 pl-xl-3">Nuova Conversazione WhatsApp</p>
				<v-spacer></v-spacer>
			</v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-0">
                <v-container fluid :class="{'px-3': $vuetify.breakpoint.mdAndUp, 'px-0': $vuetify.breakpoint.smAndDown}" class="pt-6">
                    <!-- Fields Generator -->
					<fields-generator
                        v-if="loaded"
                        :fieldsset.sync="fieldsset"
                        :model.sync="model"
                        ref="fieldsgenerator"
						class="py-0"
                    ></fields-generator>

					<!-- Manual Fields (only shown when no company selected) -->
					<v-row v-show="!model.company_id" class="px-3">
						<v-col cols="12" md="4" class="pt-0 pb-2">
							<v-autocomplete
								v-model="countryCode"
								:items="countryCodes"
								item-text="label"
								item-value="code"
								label="Prefisso"
								single-line
								outlined
								dense
								prepend-icon="mdi-earth"
								:rules="[v => !!v || 'Campo obbligatorio']"
							>
								<template v-slot:item="{ item }">
									<v-list-item-content>
										<v-list-item-title>{{ item.flag }} {{ item.country }}</v-list-item-title>
										<v-list-item-subtitle>{{ item.code }}</v-list-item-subtitle>
									</v-list-item-content>
								</template>
								<template v-slot:selection="{ item }">
									<span class="text-body-2" style="white-space: nowrap;">{{ item.flag }} {{ item.code }}</span>
								</template>
							</v-autocomplete>
						</v-col>
						<v-col cols="12" md="8" class="pt-0 pb-2">
							<v-text-field
								v-model="phoneNumberWithoutPrefix"
								label="Numero di Telefono"
								prepend-icon="mdi-phone"
								type="tel"
								outlined
								dense
								:rules="[v => !!v || 'Campo obbligatorio', v => (v && v.length <= 255) || 'Max 255 caratteri']"
								:hint="`Numero completo: ${fullPhoneNumber}`"
								persistent-hint
							></v-text-field>
						</v-col>
						<v-col cols="12" class="py-0">
							<v-text-field
								v-model="model.contact_name"
								label="Nome Contatto"
								prepend-icon="mdi-account"
								outlined
								dense
								:rules="[v => !v || v.length <= 255 || 'Max 255 caratteri']"
							></v-text-field>
						</v-col>
					</v-row>

					<!-- Phone Numbers List (shown when company is selected) -->
					<v-row v-if="model.company_id && phoneNumbers.length > 0" class="mt-2 px-3">
						<v-col cols="12">
							<v-card outlined class="round-card">
								<v-card-subtitle class="pb-2">
									<v-icon small left>mdi-phone-check</v-icon>
									Seleziona un numero di telefono
								</v-card-subtitle>
								<v-divider></v-divider>
								<v-progress-linear v-if="loadingPhoneNumbers" indeterminate color="primary"></v-progress-linear>
								<v-list v-else class="py-0">
									<v-list-item
										v-for="(phone, index) in phoneNumbers"
										:key="index"
										@click="selectPhoneNumber(phone)"
										:color="selectedPhoneNumber === phone.number ? 'primary' : ''"
										:class="{'primary darken-2': selectedPhoneNumber === phone.number && $vuetify.theme.dark, 'primary lighten-5': selectedPhoneNumber === phone.number && !$vuetify.theme.dark	}"
										class="phone-item"
										ripple
									>
										<v-list-item-avatar :color="phone.type === 'business' ? 'blue lighten-4' : 'green lighten-4'">
											<v-icon :color="phone.type === 'business' ? 'blue darken-2' : 'green darken-2'">
												{{ phone.type === 'business' ? 'mdi-office-building' : 'mdi-account' }}
											</v-icon>
										</v-list-item-avatar>
										<v-list-item-content>
											<v-list-item-title class="font-weight-medium">
												{{ phone.number }}
											</v-list-item-title>
											<v-list-item-subtitle>
												<v-icon x-small class="mr-1">{{ phone.type === 'business' ? 'mdi-domain' : 'mdi-account-circle' }}</v-icon>
												{{ phone.label }}
											</v-list-item-subtitle>
										</v-list-item-content>
										<v-list-item-action v-if="selectedPhoneNumber === phone.number">
											<v-icon color="primary">mdi-check-circle</v-icon>
										</v-list-item-action>
									</v-list-item>
								</v-list>
							</v-card>
						</v-col>
					</v-row>

					<!-- Prefix and Phone Fields (shown when company phone is selected) -->
					<v-row v-if="selectedPhoneNumber" class="px-3">
						<v-col cols="12" md="4">
							<v-autocomplete
								v-model="countryCode"
								:items="countryCodes"
								item-text="label"
								item-value="code"
								label="Prefisso"
								single-line
								outlined
								dense
								prepend-icon="mdi-earth"
								:rules="[v => !!v || 'Campo obbligatorio']"
							>
								<template v-slot:item="{ item }">
									<v-list-item-content>
										<v-list-item-title>{{ item.flag }} {{ item.country }}</v-list-item-title>
										<v-list-item-subtitle>{{ item.code }}</v-list-item-subtitle>
									</v-list-item-content>
								</template>
								<template v-slot:selection="{ item }">
									{{ item.flag }} {{ item.code }}
								</template>
							</v-autocomplete>
						</v-col>
						<v-col cols="12" md="8">
							<v-text-field
								v-model="phoneNumberWithoutPrefix"
								label="Numero di Telefono"
								prepend-icon="mdi-phone"
								type="tel"
								outlined
								dense
								:rules="[v => !!v || 'Campo obbligatorio', v => (v && v.length <= 255) || 'Max 255 caratteri']"
								:hint="`Numero completo: ${fullPhoneNumber}`"
								persistent-hint
							></v-text-field>
						</v-col>
					</v-row>

					<!-- Empty state when company selected but no phones -->
					<v-row v-if="model.company_id && !loadingPhoneNumbers && phoneNumbers.length === 0" class="mt-2 px-3">
						<v-col cols="12">
							<v-alert type="info" outlined dense>
								<v-icon left>mdi-information</v-icon>
								Nessun numero di telefono trovato per questa azienda. Aggiungi un numero di telefono all'azienda o ai suoi contatti.
							</v-alert>
						</v-col>
					</v-row>

					<!-- Template Selection -->
					<v-row class="px-3 mt-2">
						<v-col cols="12">
							<v-card outlined rounded class="rounded-lg">
								<v-card-subtitle class="pb-2">
									<v-icon small left>mdi-file-document-outline</v-icon>
									Seleziona un template WhatsApp approvato (obbligatorio per primo messaggio)
								</v-card-subtitle>
								<v-divider></v-divider>
								<v-card-text>
									<v-autocomplete
										v-model="selectedTemplate"
										:items="templates"
										:loading="loadingTemplates"
										item-text="text"
										item-value="value"
										label="Template"
										prepend-icon="mdi-file-document"
										outlined
										dense
										clearable
										return-object
										:rules="[v => !!v || 'Seleziona un template']"
									>
										<template v-slot:item="{ item }">
											<v-list-item-content>
												<v-list-item-title>{{ item.text }}</v-list-item-title>
												<v-list-item-subtitle class="text-wrap">
													{{ item.body ? (item.body.length > 50 ? item.body.slice(0, 50) + '...' : item.body) : '' }}
												</v-list-item-subtitle>
											</v-list-item-content>
										</template>
									</v-autocomplete>

									<!-- Template Name -->
									<v-subheader v-if="selectedTemplate" class="px-0 font-weight-bold">
										<v-icon small left color="primary">mdi-file-document</v-icon>
										{{ selectedTemplate.text }}
									</v-subheader>

									<!-- Template Variables -->
									<div v-if="selectedTemplate && templateVariables.length > 0" class="mt-3">
										<v-subheader class="px-0">
											<v-icon small left>mdi-variable</v-icon>
											Compila le variabili del template
										</v-subheader>
										<v-text-field
											v-for="(varInfo, varKey) in templateVariablesMap"
											:key="varKey"
											v-model="variablesValues[varKey]"
											:label="`Variabile {{${varKey}}}: ${varInfo}`"
											outlined
											dense
											:rules="[v => !!v || 'Campo obbligatorio']"
										></v-text-field>
									</div>

									<!-- Template Preview -->
									<v-card v-if="selectedTemplate" outlined class="mt-4 pa-3">
										<div class="text-caption mb-1 font-weight-bold">ANTEPRIMA MESSAGGIO:</div>
										<div class="text-body-2" v-html="renderedTemplatePreview"></div>
										<!-- Media preview if template has media (after body text) -->
										<div v-if="templateMedia && templateMedia.length > 0" class="mt-3">
											<v-img
												v-for="(mediaUrl, index) in templateMedia"
												:key="index"
												:src="mediaUrl"
												max-width="300"
												class="rounded mb-2"
											></v-img>
										</div>
									</v-card>
								</v-card-text>
							</v-card>
						</v-col>
					</v-row>
				</v-container>
			</v-card-text>
            <v-divider></v-divider>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn
					text
					@click="close"
				>
					Annulla
				</v-btn>
				<v-btn
					color="primary"
					:loading="loading"
					@click="createConversation"
				>
					Crea Conversazione
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import Vue from 'vue'
import FormsMixin from '@/mixins/FormsMixin'
import { countryCodes } from '@/utils/countryCodes'

export default {
	name: 'NewConversationDialog',
	mixins: [FormsMixin],

	props: {
		value: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
            model: {},
            fieldsset: {},
			loading: false,
			loaded: false,
			firstTime: true,
			phoneNumbers: [],
			loadingPhoneNumbers: false,
			selectedPhoneNumber: null,
			templates: [],
			loadingTemplates: false,
			selectedTemplate: null,
			variablesValues: {},
			countryCode: '+39',
			phoneNumberWithoutPrefix: '',
			countryCodes
		}
	},

	computed: {
		fullPhoneNumber() {
			if (!this.phoneNumberWithoutPrefix) return this.countryCode
			return `${this.countryCode}${this.phoneNumberWithoutPrefix}`
		},

		parsedOptions() {
			if (!this.selectedTemplate || !this.selectedTemplate.options) return null

			if (typeof this.selectedTemplate.options === 'string') {
				try {
					return JSON.parse(this.selectedTemplate.options)
				} catch (e) {
					return null
				}
			}
			return this.selectedTemplate.options
		},

		templateVariables() {
			if (!this.selectedTemplate || !this.selectedTemplate.body) return []

			// Match both numeric {{1}} and named {{first_name}} variables
			const matches = this.selectedTemplate.body.match(/\{\{([^}]+)\}\}/g)
			if (!matches) return []

			return [...new Set(matches.map(m => m.replace(/[{}]/g, '')))]
		},

		templateVariablesMap() {
			if (!this.selectedTemplate) return {}

			// Try to get variables info from options
			const options = this.parsedOptions
			if (options && options.twilio_content_json && options.twilio_content_json.variables) {
				return options.twilio_content_json.variables
			}

			// Fallback: create simple map
			const map = {}
			this.templateVariables.forEach(varKey => {
				map[varKey] = `Campo ${varKey}`
			})
			return map
		},

		templateMedia() {
			if (!this.selectedTemplate) return []

			const options = this.parsedOptions
			if (!options) return []

			// Check if template has media in options
			if (options.media && Array.isArray(options.media)) {
				return options.media
			}

			// Check in twilio_content_json
			if (options.twilio_content_json && options.twilio_content_json.types) {
				const types = options.twilio_content_json.types
				// Check twilio/media type
				if (types['twilio/media'] && types['twilio/media'].media) {
					return types['twilio/media'].media
				}
			}

			return []
		},

		renderedTemplatePreview() {
			if (!this.selectedTemplate) return ''

			let body = this.selectedTemplate.body

			Object.entries(this.variablesValues).forEach(([key, value]) => {
				const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g')
				body = body.replace(regex, value || `<span class="red--text">{{${key}}}</span>`)
			})

			return body.replace(/\n/g, '<br>')
		}
	},

	watch: {
		value(val) {
			if (val && this.firstTime) {
				this.loadFieldsInfo()
				this.loadTemplates()
				this.loadWhatsAppSettings()
			}
		},
		'model.company_id': {
			handler(newVal) {
				if (newVal) {
					this.loadPhoneNumbers(newVal)
				} else {
					this.phoneNumbers = []
					this.selectedPhoneNumber = null
					this.model.telephone_number = null
					this.model.contact_name = null
					this.model.contact_id = null
				}
			}
		},
		selectedTemplate(newVal) {
			if (newVal) {
				// Reset variables values
				this.variablesValues = {}
			}
		},
		fullPhoneNumber(newVal) {
			this.model.telephone_number = newVal
		}
	},

	methods: {

		loadWhatsAppSettings() {
			// Get default country code from store
			const defaultCountryCode = this.$store.getters.getSetting('default_country_code')
			if (defaultCountryCode) {
				this.countryCode = defaultCountryCode
			}
		},

		async loadTemplates() {
			try {
				this.loadingTemplates = true
				const response = await this.axios.get('get-whatsapp-templates')
				this.templates = response.data.data.templates || []
			} catch (error) {
				console.error('Error loading templates:', error)
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: 'Errore nel caricamento dei template',
					value: true,
					color: 'error'
				})
			} finally {
				this.loadingTemplates = false
			}
		},

		async loadPhoneNumbers(companyId) {
			try {
				this.loadingPhoneNumbers = true
				const response = await this.axios.get(`/businesses/${companyId}/phone-numbers`)
				this.phoneNumbers = response.data.phone_numbers
			} catch (error) {
				console.error('Error loading phone numbers:', error)
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: 'Errore nel caricamento dei numeri di telefono',
					value: true,
					color: 'error'
				})
			} finally {
				this.loadingPhoneNumbers = false
			}
		},

		selectPhoneNumber(phone) {
			this.selectedPhoneNumber = phone.number

			const phoneNumber = phone.number || ''

			if (!phoneNumber.startsWith('+')) {
				// No + prefix, user must select prefix manually
				this.countryCode = ''
				this.phoneNumberWithoutPrefix = phoneNumber
			} else {
				// Number has + prefix, try to detect and extract it
				const numberWithoutPlus = phoneNumber.substring(1)

				// Sort prefixes by length (longest first) to match correctly
				const sortedPrefixes = [...this.countryCodes].sort((a, b) => {
					const aLen = a.code.replace('+', '').length
					const bLen = b.code.replace('+', '').length
					return bLen - aLen
				})

				let prefixFound = false
				for (const countryCode of sortedPrefixes) {
					const prefixDigits = countryCode.code.replace('+', '')
					if (numberWithoutPlus.startsWith(prefixDigits)) {
						// Found matching prefix
						this.countryCode = countryCode.code
						this.phoneNumberWithoutPrefix = numberWithoutPlus.substring(prefixDigits.length)
						prefixFound = true
						break
					}
				}

				if (!prefixFound) {
					// Prefix not recognized, leave empty
					this.countryCode = ''
					this.phoneNumberWithoutPrefix = phoneNumber
				}
			}

			// Set contact info
			this.$set(this.model, 'contact_name', phone.owner)
			if (phone.contact_id) {
				this.$set(this.model, 'contact_id', phone.contact_id)
			} else {
				// If it's a business phone, remove contact_id
				this.$set(this.model, 'contact_id', null)
			}
		},

		async loadFieldsInfo() {
			try {
				const response = await this.axios.get('/whatsappconversations-info')
				this.fieldsset = response.data.fields
				this.loaded = true
				this.firstTime = false

				// Set default values if specified
				if (this.fieldsset.fields) {
					this.fieldsset.fields.forEach(field => {
						if (field.default) {
							this.$set(this.model, field.key, field.default)
						}
					})
				}
			} catch (error) {
				console.error('Error loading conversation fields:', error)
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: 'Errore nel caricamento dei campi',
					value: true,
					color: 'error'
				})
			}
		},

		async createConversation() {
			try {
				// Validate phone number is selected
				if (!this.model.telephone_number) {
					this.$store.commit('setSnackbar', {
						timeout: 6000,
						text: 'Seleziona un numero di telefono per continuare',
						value: true,
						color: 'warning'
					})
					return
				}

				// Validate template is selected
				if (!this.selectedTemplate) {
					this.$store.commit('setSnackbar', {
						timeout: 6000,
						text: 'Seleziona un template per continuare',
						value: true,
						color: 'warning'
					})
					return
				}

				// Validate all variables are filled
				const missingVars = this.templateVariables.filter(varKey => !this.variablesValues[varKey])
				if (missingVars.length > 0) {
					this.$store.commit('setSnackbar', {
						timeout: 6000,
						text: `Compila tutte le variabili richieste: ${missingVars.join(', ')}`,
						value: true,
						color: 'warning'
					})
					return
				}

				// Validate form
				if (this.$refs.fieldsgenerator) {
					const isValid = await this.$refs.fieldsgenerator.$validator.validateAll()
					if (!isValid) {
						return this.handleValidationErrorAdvanced()
					}
				}

				this.loading = true

				// Create conversation
				const response = await this.axios.post('/whatsappconversations', this.model)
				const conversation = response.data

				// Send template message
				try {
					await this.axios.post('/whatsappmessages-send-template', {
						conversation_id: conversation.id,
						template_id: this.selectedTemplate.value,
						template_variables: this.variablesValues
					})

					this.$emit('created', conversation)
					this.close()

					this.$store.commit('setSnackbar', {
						timeout: 6000,
						text: 'Conversazione creata e messaggio template inviato con successo',
						value: true,
						color: 'success'
					})
				} catch (messageError) {
					console.error('Error sending template message:', messageError)
					// Conversation was created but message failed
					this.$emit('created', conversation)
					this.close()

					this.$store.commit('setSnackbar', {
						timeout: 8000,
						text: 'Conversazione creata ma errore nell\'invio del messaggio template',
						value: true,
						color: 'warning'
					})
				}
			} catch (error) {
				console.error('Error creating conversation:', error)
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: error.response?.data?.message || 'Errore nella creazione della conversazione',
					value: true,
					color: 'error'
				})
			} finally {
				this.loading = false
			}
		},

		close() {
			this.$emit('input', false)
			this.resetForm()
		},

		resetForm() {
			this.model = {}
			this.phoneNumbers = []
			this.selectedPhoneNumber = null
			this.selectedTemplate = null
			this.variablesValues = {}
			if (this.$refs.fieldsgenerator && this.$refs.fieldsgenerator.$validator) {
				this.$refs.fieldsgenerator.$validator.reset()
			}
		}
	}
}
</script>

<style scoped>
.v-dialog {
	overflow-y: auto
}

.phone-item {
	cursor: pointer;
	transition: all 0.2s ease
}

.phone-item:hover {
	background-color: rgba(0, 0, 0, 0.04)
}

.rounded-lg {
	border-radius: 12px
}
</style>